import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ModelsScreen.css';

const ModelsScreen = () => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Simulate fetching models data
  useEffect(() => {
    // Mock data
    const mockModels = [
      {
        id: 'model1',
        name: 'Smart Home Hub',
        category: 'IoT',
        image: '/images/models/smart-home.jpg',
        description: 'Complete IoT solution for controlling home devices with voice commands and mobile app.',
        components: 18,
        downloads: 2435,
        difficulty: 'Intermediate'
      },
      {
        id: 'model2',
        name: 'Agricultural Monitoring System',
        category: 'Environmental',
        image: '/images/models/agriculture.jpg',
        description: 'Solar-powered system for monitoring soil moisture, temperature, and sunlight for optimal farming.',
        components: 12,
        downloads: 1876,
        difficulty: 'Intermediate'
      },
      {
        id: 'model3',
        name: 'Autonomous Robot Platform',
        category: 'Robotics',
        image: '/images/models/robot.jpg',
        description: 'Versatile robot base with obstacle avoidance, programmable pathfinding and sensor integration.',
        components: 24,
        downloads: 3254,
        difficulty: 'Advanced'
      },
      {
        id: 'model4',
        name: 'Weather Station',
        category: 'Environmental',
        image: '/images/models/weather.jpg',
        description: 'Compact weather station with temperature, humidity, pressure, and rainfall sensors with WiFi connectivity.',
        components: 10,
        downloads: 4128,
        difficulty: 'Beginner'
      },
      {
        id: 'model5',
        name: 'Smart Wearable Fitness Tracker',
        category: 'Wearables',
        image: '/images/models/fitness.jpg',
        description: 'Wearable device that tracks steps, heart rate, and sleep patterns with Bluetooth connectivity.',
        components: 15,
        downloads: 1893,
        difficulty: 'Intermediate'
      },
      {
        id: 'model6',
        name: 'LED Matrix Display',
        category: 'Educational',
        image: '/images/models/led-matrix.jpg',
        description: 'Programmable 8x8 LED matrix display for scrolling messages and simple animations.',
        components: 8,
        downloads: 3750,
        difficulty: 'Beginner'
      },
      {
        id: 'model7',
        name: 'Industrial Sensor Gateway',
        category: 'Industrial',
        image: '/images/models/industrial.jpg',
        description: 'Rugged IoT gateway for collecting and transmitting data from multiple industrial sensors.',
        components: 22,
        downloads: 1245,
        difficulty: 'Advanced'
      },
      {
        id: 'model8',
        name: 'Smart Irrigation Controller',
        category: 'Environmental',
        image: '/images/models/irrigation.jpg',
        description: 'Automated irrigation system with soil moisture sensing and weather forecast integration.',
        components: 14,
        downloads: 2876,
        difficulty: 'Intermediate'
      }
    ];
    
    // Simulate API delay
    setTimeout(() => {
      setModels(mockModels);
      setFilteredModels(mockModels);
      setLoading(false);
    }, 800);
  }, []);
  
  // Filter models when filter or search changes
  useEffect(() => {
    let result = [...models];
    
    // Apply category filter
    if (activeFilter !== 'all') {
      result = result.filter(model => model.category.toLowerCase() === activeFilter.toLowerCase());
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        model => 
          model.name.toLowerCase().includes(query) || 
          model.description.toLowerCase().includes(query) ||
          model.category.toLowerCase().includes(query)
      );
    }
    
    setFilteredModels(result);
  }, [activeFilter, searchQuery, models]);
  
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const categories = [
    { id: 'all', name: 'All Models' },
    { id: 'iot', name: 'IoT' },
    { id: 'robotics', name: 'Robotics' },
    { id: 'environmental', name: 'Environmental' },
    { id: 'wearables', name: 'Wearables' },
    { id: 'educational', name: 'Educational' },
    { id: 'industrial', name: 'Industrial' }
  ];
  
  if (loading) {
    return (
      <div className="models-loading">
        <div className="loading-spinner"></div>
        <p>Loading models...</p>
      </div>
    );
  }

  return (
    <div className="models-screen">
      <div className="models-header">
        <h1>Design Models</h1>
        <p>Browse our collection of ready-to-build electronic design models</p>
        
        <div className="models-search">
          <input 
            type="text" 
            placeholder="Search models by name, category or description..." 
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button className="search-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="models-filters">
        {categories.map(category => (
          <button 
            key={category.id}
            className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
            onClick={() => handleFilterChange(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      
      {filteredModels.length > 0 ? (
        <div className="models-grid">
          {filteredModels.map(model => (
            <div className="model-card" key={model.id}>
              <div className="model-image">
                <img 
                  src={model.image} 
                  alt={model.name}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/300x200?text=${model.name.replace(' ', '+')}`;
                  }}
                />
                <span className="model-category">{model.category}</span>
                <span className={`model-difficulty ${model.difficulty.toLowerCase()}`}>
                  {model.difficulty}
                </span>
              </div>
              
              <div className="model-content">
                <h3>{model.name}</h3>
                <p>{model.description}</p>
                
                <div className="model-meta">
                  <span className="meta-item">
                    <i className="meta-icon">🔌</i>
                    {model.components} Components
                  </span>
                  <span className="meta-item">
                    <i className="meta-icon">⬇️</i>
                    {model.downloads.toLocaleString()} Downloads
                  </span>
                </div>
                
                <div className="model-actions">
                  <button className="view-model-btn">View Details</button>
                  <button className="download-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-models">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="#94a3b8">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <h3>No models found</h3>
          <p>Try adjusting your search or filter criteria</p>
          <button className="reset-btn" onClick={() => {
            setActiveFilter('all');
            setSearchQuery('');
          }}>
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ModelsScreen;